import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, ChevronLeft, Map, Menu as MenuIcon, Home, UserCircle, Receipt, X as XIcon, AlertTriangle, DollarSign, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PanicButton from "@/components/PanicButton";
import SupportEdgeTab from "@/components/SupportEdgeTab";
// v2.6.9 batch BF-2 · VerifiedBadge + POSTerminalDialog removed from driver
// Layout per user request (no green Online status, no in-app POS dialog).
import { useConfig } from "@/contexts/ConfigContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useState, useEffect, useCallback } from "react";
import { getMapPref, setMapPref } from "@/components/NavigateButton";
import DriverDispatchModal from "@/components/DriverDispatchModal";
import ModeSwitcher from "@/components/ModeSwitcher";
import QuickSwitcherCommand from "@/components/QuickSwitcherCommand";
import DisplaySettingsMenu from "@/components/DisplaySettingsMenu";
import useDisplayPrefs from "@/hooks/useDisplayPrefs";
import api from "@/lib/api";

/**
 * Layout — v2.6.3 mobile-friendly redesign.
 * Desktop (≥ sm): horizontal toolbar with all controls visible.
 * Mobile (< sm): a single hamburger button opens a slide-down drawer with
 * every action stacked vertically (large 48px hit targets). Drivers reported
 * the cramped horizontal toolbar made profile editing hard on phones.
 */
export default function Layout({ children, title, back = true }) {
  const { driver, logout } = useAuth();
  const { config } = useConfig();
  const nav = useNavigate();
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const [pref, setPref] = useState(getMapPref());
  const changePref = (v) => { setMapPref(v); setPref(v); };
  const [me, setMe] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // v2.6.8 batch M · pick up admin-managed site logo if configured
  const [branding, setBranding] = useState({});
  useEffect(() => { api.get("/auth/me").then(r => setMe(r.data)).catch((err) => console.warn("auth/me failed", err?.response?.status)); }, []);
  useEffect(() => { api.get("/branding").then(r => setBranding(r.data || {})).catch(()=>{}); }, []);
  const closeMenu = () => setMenuOpen(false);

  // v2.8.5 · Driver app display density + text size — persisted per account.
  const fetchDriverPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/ui-preferences");
      return {
        compact: !!data?.compact,
        autoCompact: !!data?.auto_compact,
        fontScale: data?.font_scale || "md",
      };
    } catch { return null; }
  }, []);
  const persistDriverPrefs = useCallback((p) => {
    const body = {};
    if ("compact" in p) body.compact = p.compact;
    if ("autoCompact" in p) body.auto_compact = p.autoCompact;
    if ("fontScale" in p) body.font_scale = p.fontScale;
    if (Object.keys(body).length) api.patch("/driver/ui-preferences", body).catch(() => {});
  }, []);
  const disp = useDisplayPrefs({ prefix: "rtg_driver", fetchPrefs: fetchDriverPrefs, persistPrefs: persistDriverPrefs });

  const roleLabel = (driver?.role || "driver").toUpperCase();

  return (
    <div className={`min-h-screen bg-[#0c0d10] text-zinc-100 ui-scale-${disp.fontScale}${disp.effectiveCompact ? " ui-compact" : ""}`} data-testid="driver-root" data-compact={disp.effectiveCompact ? "1" : "0"} data-font-scale={disp.fontScale}>
      <QuickSwitcherCommand mode="driver"/>
      {/* z-[60] keeps the hamburger above the sonner toast layer (z-50) so the
         menu remains tappable even when a 'Ride declined' toast is visible. */}
      <header className="sticky top-0 z-[60] backdrop-blur-xl bg-[#0c0d10]/80 border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between gap-2">
          {/* LEFT — back arrow + logo + title */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {back && !isHome && (
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="icon"
                onClick={() => nav(-1)}
                className="text-zinc-300 hover:text-amber-400 h-9 w-9"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
            )}
            <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
              <img
                src={branding.site_logo_url || "/buller-taxi-crest.png"}
                alt="Buller Taxi"
                className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0 object-contain"
                data-testid="header-logo"
              />
              <div className="min-w-0">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold leading-none">BULLER TAXI</div>
                <div className="text-xs sm:text-sm font-bold truncate leading-tight">{title || "Dispatch"}</div>
              </div>
            </Link>
          </div>

          {/* RIGHT — desktop toolbar (≥ sm) vs mobile hamburger (< sm) */}
          <div className="hidden sm:flex items-center gap-2">
            {/* v2.6.9 batch BF-2 · VerifiedBadge (green Online status) removed per user request. */}
            {/* v2.6.9 batch CI · "Dispatch Mode" — drivers with a dispatch
                role (admin/supervisor/dispatcher/team_leader) can hop into
                /admin in a new tab. Hidden for plain drivers. */}
            <OpenDispatchModeButton driver={driver}/>
            <ModeSwitcher mode="driver"/>
            <DisplaySettingsMenu
              compact={disp.compact}
              autoCompact={disp.autoCompact}
              fontScale={disp.fontScale}
              effectiveCompact={disp.effectiveCompact}
              setCompact={disp.setCompact}
              setAutoCompact={disp.setAutoCompact}
              setFontScale={disp.setFontScale}
              align="end"
              buttonClassName="bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-amber-400"
            />
            <Link to="/earnings" data-testid="earnings-link" className="text-xs text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1"><DollarSign className="h-3.5 w-3.5"/>Earnings</Link>
            <Link to="/profile" data-testid="profile-link" className="text-xs text-zinc-400 hover:text-amber-400">My Profile</Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="map-pref-btn" variant="ghost" size="icon" className="text-zinc-300 hover:text-amber-400">
                  <Map className="h-4 w-4"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
                <DropdownMenuLabel>Navigation app</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-700"/>
                <DropdownMenuItem data-testid="map-pref-auto" onClick={() => changePref("auto")} className={pref==="auto" ? "text-amber-400" : ""}>Auto (detect phone)</DropdownMenuItem>
                <DropdownMenuItem data-testid="map-pref-google" onClick={() => changePref("google")} className={pref==="google" ? "text-amber-400" : ""}>Google Maps</DropdownMenuItem>
                <DropdownMenuItem data-testid="map-pref-apple" onClick={() => changePref("apple")} className={pref==="apple" ? "text-amber-400" : ""}>Apple Maps</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="text-xs text-zinc-400" data-testid="driver-name">{driver?.name}</span>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={() => { logout(); nav("/login"); }}
              className="text-zinc-400 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>

          {/* MOBILE — single hamburger that opens the drawer below */}
          <div className="sm:hidden flex items-center gap-1">
            {/* v2.6.9 batch BF-2 · VerifiedBadge removed from mobile header too. */}
            <Button
              data-testid="mobile-menu-btn"
              variant="ghost"
              size="icon"
              onClick={() => setMenuOpen((o) => !o)}
              className="text-zinc-200 hover:text-amber-400 h-10 w-10"
              aria-label="Open menu"
            >
              {menuOpen ? <XIcon className="h-5 w-5"/> : <MenuIcon className="h-5 w-5"/>}
            </Button>
          </div>
        </div>

        {/* v2.6.9 batch BF-2 · DESKTOP toolbar — POS Terminal removed per
            user request. Drivers no longer have an in-app POS dialog;
            credit-card payments are now handled exclusively via physical
            Square Reader hardware. */}

        {/* MOBILE — slide-down menu drawer (visible only when menuOpen) */}
        {menuOpen && (
          <div className="sm:hidden border-t border-zinc-800 bg-[#0c0d10]/95 backdrop-blur-xl" data-testid="mobile-menu-drawer">
            <div className="px-4 py-3 space-y-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold py-1">
                {driver?.name} · <span className="text-amber-400">{roleLabel}</span>
              </div>
              <Link
                to="/dashboard"
                onClick={closeMenu}
                data-testid="mobile-home-link"
                className="flex items-center gap-3 px-3 py-3 rounded-md text-zinc-100 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-800/80 border border-zinc-800 text-sm font-medium"
              >
                <Home className="h-4 w-4 text-amber-400"/> App Home
              </Link>
              <Link
                to="/profile"
                onClick={closeMenu}
                data-testid="mobile-profile-link"
                className="flex items-center gap-3 px-3 py-3 rounded-md text-zinc-100 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-800/80 border border-zinc-800 text-sm font-medium"
              >
                <UserCircle className="h-4 w-4 text-amber-400"/> Edit Profile
              </Link>
              <Link
                to="/earnings"
                onClick={closeMenu}
                data-testid="mobile-earnings-link"
                className="flex items-center gap-3 px-3 py-3 rounded-md text-zinc-100 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-800/80 border border-amber-400/30 text-sm font-medium"
              >
                <DollarSign className="h-4 w-4 text-amber-400"/> Earnings
              </Link>
              <Link
                to="/trips"
                onClick={closeMenu}
                data-testid="mobile-trips-link"
                className="flex items-center gap-3 px-3 py-3 rounded-md text-zinc-100 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-800/80 border border-zinc-800 text-sm font-medium"
              >
                <Receipt className="h-4 w-4 text-amber-400"/> Trip Log
              </Link>
              {/* v2.6.9 batch Q · TAXIcab Incident Commander entrypoint */}
              <Link
                to="/incident-report"
                onClick={closeMenu}
                data-testid="mobile-incident-link"
                className="flex items-center gap-3 px-3 py-3 rounded-md text-zinc-100 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-800/80 border border-amber-400/30 text-sm font-medium"
              >
                <AlertTriangle className="h-4 w-4 text-amber-400"/> TAXIcab Incident Commander
              </Link>
              {/* v2.6.9 batch CI · Mobile Dispatch Mode entry (only for dispatch
                  roles — the button itself renders null otherwise). */}
              <OpenDispatchModeButton driver={driver} mobile onClick={closeMenu}/>
              {/* Map navigation preference (collapsed inline picker) */}
              <div className="px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800">
                <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2"><Map className="h-3.5 w-3.5"/> Navigation app</div>
                <div className="grid grid-cols-3 gap-1">
                  {[
                    { v: "auto", label: "Auto" },
                    { v: "google", label: "Google" },
                    { v: "apple", label: "Apple" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      onClick={() => changePref(opt.v)}
                      data-testid={`mobile-map-pref-${opt.v}`}
                      className={`text-xs py-2 rounded border ${pref === opt.v ? "border-amber-400 bg-amber-400/10 text-amber-300" : "border-zinc-700 text-zinc-300"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* v2.8.5 · Display density + text size for the driver app */}
              <div className="px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800" data-testid="mobile-display-settings">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-zinc-400">Display · spacing &amp; text size</div>
                  <DisplaySettingsMenu
                    compact={disp.compact}
                    autoCompact={disp.autoCompact}
                    fontScale={disp.fontScale}
                    effectiveCompact={disp.effectiveCompact}
                    setCompact={disp.setCompact}
                    setAutoCompact={disp.setAutoCompact}
                    setFontScale={disp.setFontScale}
                    align="end"
                    buttonClassName="bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:text-amber-400"
                  />
                </div>
              </div>
              {/* v2.6.9 batch BF-2 · POS Terminal removed from mobile drawer too. */}
              <button
                data-testid="mobile-logout-btn"
                onClick={() => { closeMenu(); logout(); nav("/login"); }}
                className="w-full flex items-center justify-center gap-2 mt-2 px-3 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 hover:bg-red-500/20 active:bg-red-500/30 text-sm font-bold"
              >
                <LogOut className="h-4 w-4"/> Sign out
              </button>
            </div>
          </div>
        )}
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-5 py-5 sm:py-6 fade-in">{children}</main>
      <DriverDispatchModal/>
      {/* v2.6.7 batch J — single right-edge SOS tab on every driver page. */}
      <PanicButton/>
      {/* v2.6.9 batch BL · Floating Safety & Support stack — sits above PanicButton */}
      <SupportEdgeTab/>
    </div>
  );
}

/**
 * v2.6.9 batch CI · OpenDispatchModeButton — Driver-page counterpart to the
 * admin-side OpenRiderMode / OpenDriverMode buttons. Only renders for
 * drivers whose role is admin / supervisor / dispatcher / team_leader.
 * Hits POST /api/driver/staff/dispatch-mode to mint an admin JWT, stores
 * it under the same localStorage keys adminApi reads from, and opens
 * /admin in a new tab. Plain drivers see nothing.
 *
 * Two render modes:
 *   default (desktop toolbar) — compact outline button with Shield icon
 *   mobile=true — full-width drawer row, larger hit target
 */
function OpenDispatchModeButton({ driver, mobile = false, onClick }) {
  const [busy, setBusy] = useState(false);
  const role = (driver?.role || "driver").toLowerCase();
  if (!["admin", "supervisor", "dispatcher", "team_leader"].includes(role)) return null;
  const open = async () => {
    if (onClick) onClick();
    setBusy(true);
    try {
      const { data } = await api.post("/driver/staff/dispatch-mode");
      // adminApi.js + admin/AuthContext read from these exact keys.
      localStorage.setItem("admin_token", data.access_token);
      localStorage.setItem("admin_user", JSON.stringify(data.admin));
      toast.success("Dispatch mode opened in a new tab");
      window.open("/admin", "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open dispatch mode");
    } finally { setBusy(false); }
  };
  if (mobile) {
    return (
      <button
        type="button"
        onClick={open}
        disabled={busy}
        data-testid="driver-open-dispatch-mode-mobile"
        className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-zinc-100 bg-zinc-900 hover:bg-zinc-800 active:bg-zinc-800/80 border border-amber-400/30 text-sm font-medium disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin text-amber-400"/> : <Shield className="h-4 w-4 text-amber-400"/>}
        Dispatch Mode
      </button>
    );
  }
  return (
    <Button
      onClick={open}
      disabled={busy}
      variant="outline"
      size="sm"
      data-testid="driver-open-dispatch-mode"
      className="bg-zinc-950 border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-8 text-xs"
      title="Open the dispatch console in a new tab"
    >
      {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : <Shield className="h-3 w-3 mr-1"/>}Dispatch Mode
    </Button>
  );
}

